import React, { useState } from 'react';
import SEO from '../components/SEO';
import seoData from '../config/seoData';
import { Phone, MessageCircle, MapPin, CheckCircle, Wrench, Sparkles, Menu, X, ChevronRight, ArrowRight, AlertCircle, Instagram } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { useNavigate, Link } from 'react-router-dom';

const LimpezaPisoPosObra = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleWhatsAppClick = (contexto = 'piso') => {
    const mensagens = {
      piso: 'Olá! Preciso de limpeza de piso pós-obra. Gostaria de receber um orçamento.',
      orcamento: 'Olá! Gostaria de solicitar orçamento de limpeza de piso pós-obra.',
      info: 'Olá! Gostaria de saber mais sobre limpeza de piso pós-obra.'
    };
    
    const message = encodeURIComponent(mensagens[contexto] || mensagens.piso);
    window.open(`https://wa.me/5531971823249?text=${message}`, '_blank');
  };

  const tiposPiso = [
    {
      tipo: 'Concreto',
      caracteristicas: 'Piso mais comum em galpões, garagens e áreas industriais. Resistente, mas poroso.',
      residuosTipicos: ['Cimento', 'Argamassa', 'Poeira pesada', 'Manchas de óleo'],
      metodoLimpeza: 'Varredura inicial, aplicação de produto adequado, lavagem mecanizada com disco/pad específico, enxágue e secagem.',
      cuidados: 'Verificar se há tratamento superficial (selador, verniz). Produto muito ácido pode corroer.',
      equipamento: 'Lavadora de piso com disco verde ou preto (conforme nível de sujeira)'
    },
    {
      tipo: 'Cerâmica',
      caracteristicas: 'Comum em áreas comerciais, residenciais e alguns ambientes industriais.',
      residuosTipicos: ['Rejunte em excesso', 'Cimento', 'Poeira', 'Marcas de obra'],
      metodoLimpeza: 'Remoção manual de excesso de rejunte, aplicação de produto, lavagem mecanizada suave, enxágue.',
      cuidados: 'Cuidado com rejuntes recém-aplicados. Pads muito abrasivos podem riscar cerâmicas esmaltadas.',
      equipamento: 'Lavadora com pad branco ou vermelho (baixa abrasividade)'
    },
    {
      tipo: 'Porcelanato',
      caracteristicas: 'Alta resistência e baixa porosidade. Acabamento liso e elegante.',
      residuosTipicos: ['Rejunte', 'Manchas superficiais', 'Poeira fina', 'Resíduos de obra'],
      metodoLimpeza: 'Limpeza delicada com produtos neutros ou levemente alcalinos, lavagem com pad suave, secagem.',
      cuidados: 'Evitar produtos muito ácidos ou abrasivos. Não usar pads muito agressivos que podem arranhar.',
      equipamento: 'Lavadora com pad branco (mínima abrasividade)'
    },
    {
      tipo: 'Piso Industrial (Epóxi/Poliuretano)',
      caracteristicas: 'Revestimento industrial liso, impermeável e resistente. Usado em indústrias e laboratórios.',
      residuosTipicos: ['Poeira', 'Resíduos químicos', 'Manchas leves', 'Marcas de obra'],
      metodoLimpeza: 'Limpeza técnica com produtos compatíveis, lavagem suave, evitar solventes agressivos.',
      cuidados: 'Não usar produtos que ataquem o revestimento. Verificar compatibilidade química antes.',
      equipamento: 'Lavadora com pad branco ou vermelho, produtos específicos'
    },
    {
      tipo: 'Mármore e Granito',
      caracteristicas: 'Pedras naturais nobres. Mármore é sensível a ácidos, granito é mais resistente.',
      residuosTipicos: ['Manchas de cimento', 'Poeira', 'Rejunte', 'Riscos superficiais'],
      metodoLimpeza: 'Limpeza com produtos neutros, lavagem delicada, polimento em casos específicos.',
      cuidados: 'NUNCA usar produtos ácidos em mármore. Granito tolera melhor, mas preferir produtos neutros.',
      equipamento: 'Lavadora com pad suave, aspiração prévia de pó'
    },
    {
      tipo: 'Piso Vinílico e Laminado',
      caracteristicas: 'Pisos comerciais modernos. Sensíveis ao excesso de umidade.',
      residuosTipicos: ['Poeira', 'Marcas leves', 'Resíduos superficiais'],
      metodoLimpeza: 'Limpeza úmida controlada, produtos específicos, secagem imediata.',
      cuidados: 'Evitar excesso de água. Não usar pads abrasivos. Secar rapidamente.',
      equipamento: 'Lavadora com pad branco, baixa liberação de água'
    }
  ];

  const fatoresDefinemMetodo = [
    {
      fator: 'Tipo de Piso',
      impacto: 'Define produtos químicos, abrasividade do pad/disco, pressão do equipamento',
      icon: <Sparkles className="w-6 h-6" />
    },
    {
      fator: 'Tipo de Resíduo',
      impacto: 'Cimento requer produto alcalino, gordura requer desengraxante, poeira só água',
      icon: <AlertCircle className="w-6 h-6" />
    },
    {
      fator: 'Nível de Sujeira',
      impacto: 'Define quantidade de etapas (pré-varredura, lavagem, enxágue, polimento)',
      icon: <CheckCircle className="w-6 h-6" />
    },
    {
      fator: 'Metragem da Área',
      impacto: 'Define porte do equipamento (walk-behind pequena ou ride-on grande)',
      icon: <Wrench className="w-6 h-6" />
    }
  ];

  const faqPiso = [
    {
      question: 'Como saber qual o tipo do meu piso?',
      answer: 'Se você não souber o tipo exato do piso, nossa equipe técnica avalia no local. Através de testes simples identificamos se é cerâmica, porcelanato, concreto, epóxi, etc., e definimos o método adequado.'
    },
    {
      question: 'Vocês removem manchas de cimento e argamassa?',
      answer: 'Sim. Utilizamos produtos adequados para cada tipo de piso. Cimento e argamassa são removidos com produtos alcalinos ou ácidos controlados, conforme a superfície. Em pisos delicados (mármore, por exemplo), o processo é ainda mais cuidadoso.'
    },
    {
      question: 'A limpeza pode danificar meu piso novo?',
      answer: 'Não, se feita corretamente. Por isso avaliamos o tipo de piso antes de iniciar. Utilizamos produtos e pads compatíveis com cada superfície. Nossa experiência técnica garante limpeza eficaz sem danificar o acabamento.'
    },
    {
      question: 'Quanto tempo o piso demora para secar?',
      answer: 'Com lavadoras profissionais que possuem sistema de sucção, o piso fica praticamente seco ao final do processo. Em áreas bem ventiladas, a secagem completa ocorre em 10 a 30 minutos. Em áreas fechadas pode levar um pouco mais.'
    },
    {
      question: 'Vocês fazem polimento de piso após a limpeza?',
      answer: 'A limpeza pós-obra não inclui polimento técnico (cristalização ou vitrificação). Porém, dependendo do tipo de piso e do serviço contratado, podemos aplicar produtos de brilho ou realizar acabamento específico. Consulte para serviços adicionais.'
    },
    {
      question: 'Qual o custo por m² da limpeza de piso?',
      answer: 'O valor varia conforme tipo de piso, nível de sujeira, metragem e cidade. Em média, pisos comerciais simples custam entre R$ 3,00 e R$ 6,00/m². Pisos industriais ou com sujeira pesada podem custar mais. Solicite orçamento informando tipo de piso e metragem.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title={seoData['/limpeza-piso-pos-obra'].title}
        description={seoData['/limpeza-piso-pos-obra'].description}
        canonical={seoData['/limpeza-piso-pos-obra'].canonical}
      />

      {/* Header */}
      <header className="bg-slate-900 text-white py-4 px-6 sticky top-0 z-40 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <img 
              src="https://customer-assets.emergentagent.com/job_locacao-pisos/artifacts/9nveujmi_file_00000000e2f071f5a40c0fc04e1c9bed.png" 
              alt="WellTec Logo" 
              className="h-16 w-auto"
            />
          </div>
          
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/" className="hover:text-blue-400 transition-colors">Início</Link>
            <Link to="/servicos" className="hover:text-blue-400 transition-colors">Serviços</Link>
            <Link to="/limpeza-pos-obra" className="text-green-400 font-semibold">Limpeza Pós-Obra</Link>
            <Link to="/locacao-lavadora-piso" className="hover:text-blue-400 transition-colors">Locação</Link>
            <Link to="/blog" className="hover:text-blue-400 transition-colors">Blog</Link>
            <a href="https://www.instagram.com/welltecbrasil/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors">
              <Instagram className="w-5 h-5" />
            </a>
            <Button onClick={handleWhatsAppClick} className="bg-green-500 hover:bg-green-600">
              <MessageCircle className="w-4 h-4 mr-2" />
              WhatsApp
            </Button>
          </div>

          <button className="md:hidden text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-slate-900 border-t border-slate-700 shadow-xl">
            <div className="flex flex-col space-y-1 p-4">
              <button onClick={() => { navigate('/'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">Início</button>
              <button onClick={() => { navigate('/servicos'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">Serviços</button>
              <button onClick={() => { navigate('/limpeza-pos-obra'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors text-green-400 font-semibold">Limpeza Pós-Obra</button>
              <button onClick={() => { navigate('/locacao-lavadora-piso'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">Locação</button>
              <button onClick={() => { navigate('/blog'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">Blog</button>
              <Button onClick={() => { handleWhatsAppClick(); setMobileMenuOpen(false); }} className="bg-green-500 hover:bg-green-600 w-full mt-2">
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-green-900 via-slate-800 to-slate-900 text-white py-24 md:py-32 overflow-hidden">
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-block bg-green-600 text-white px-4 py-2 rounded-full text-sm font-bold mb-6">
              GUIA TÉCNICO COMPLETO
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Limpeza de Piso Pós-Obra em BH e Região
            </h1>
            
            <p className="text-xl md:text-2xl text-green-100 mb-6 leading-relaxed font-semibold">
              Método específico para cada tipo de piso: concreto, cerâmica, porcelanato, epóxi, mármore e mais
            </p>

            <p className="text-lg text-gray-300 mb-8 max-w-3xl">
              O procedimento correto de limpeza depende do tipo de piso e dos resíduos presentes. Nossa avaliação técnica define equipamento, produto, pad/disco e quantidade de etapas necessárias.
            </p>

            <p className="text-sm text-green-200 mb-10 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Atendimento em Belo Horizonte e Região Metropolitana
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => handleWhatsAppClick('orcamento')}
                size="lg"
                className="bg-green-500 hover:bg-green-600 text-white px-8 py-6 text-lg shadow-xl hover:scale-105 transition-all"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Solicitar Orçamento
              </Button>
              
              <Button 
                onClick={() => navigate('/limpeza-pos-obra')}
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-slate-900 px-8 py-6 text-lg"
              >
                Ver Todos os Serviços
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* O Que Define o Método de Limpeza */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                O Que Define o Método de Limpeza?
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Não existe uma "receita única" para limpeza de piso pós-obra. O procedimento correto depende de vários fatores técnicos.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {fatoresDefinemMetodo.map((item, index) => (
                <Card key={index} className="border-2 border-gray-100 hover:border-green-500 transition-all">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-green-100 text-green-600 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                        {item.icon}
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">{item.fator}</h3>
                        <p className="text-gray-600 text-sm">{item.impacto}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Guia por Tipo de Piso */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Guia de Limpeza por Tipo de Piso
              </h2>
              <p className="text-lg text-gray-600">
                Método específico para cada superfície
              </p>
            </div>

            <div className="space-y-6">
              {tiposPiso.map((piso, index) => (
                <Card key={index} className="border-2 border-gray-100 hover:border-green-500 hover:shadow-xl transition-all">
                  <CardContent className="p-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                      <span className="bg-green-600 text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">
                        {index + 1}
                      </span>
                      {piso.tipo}
                    </h3>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <p className="text-gray-700 mb-4">
                          <strong>Características:</strong> {piso.caracteristicas}
                        </p>
                        <p className="text-gray-700 mb-4">
                          <strong>Resíduos Típicos:</strong>
                        </p>
                        <div className="flex flex-wrap gap-2 mb-4">
                          {piso.residuosTipicos.map((residuo, idx) => (
                            <span key={idx} className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm">
                              {residuo}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      <div>
                        <p className="text-gray-700 mb-3">
                          <strong>Método:</strong> {piso.metodoLimpeza}
                        </p>
                        <p className="text-blue-700 mb-3 text-sm">
                          <strong>⚠️ Cuidados:</strong> {piso.cuidados}
                        </p>
                        <p className="text-green-700 text-sm">
                          <strong>🔧 Equipamento:</strong> {piso.equipamento}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Por Que Não Fazer Manualmente? */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Por Que Utilizar Equipamentos Profissionais?
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <Card className="border-2 border-gray-100 hover:border-green-500 transition-all">
                <CardContent className="p-6 text-center">
                  <div className="bg-green-100 text-green-600 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto">
                    <CheckCircle className="w-8 h-8" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Maior Eficiência</h3>
                  <p className="text-gray-600 text-sm">
                    Lavadoras removem sujeira aderida que a limpeza manual não consegue
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-100 hover:border-green-500 transition-all">
                <CardContent className="p-6 text-center">
                  <div className="bg-green-100 text-green-600 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto">
                    <Sparkles className="w-8 h-8" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Resultado Uniforme</h3>
                  <p className="text-gray-600 text-sm">
                    Limpeza mecanizada garante padrão consistente em toda área
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-100 hover:border-green-500 transition-all">
                <CardContent className="p-6 text-center">
                  <div className="bg-green-100 text-green-600 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto">
                    <Wrench className="w-8 h-8" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Menos Tempo</h3>
                  <p className="text-gray-600 text-sm">
                    Equipamentos profissionais reduzem drasticamente o tempo de execução
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Central */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-green-700 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Não Sabe Qual Tipo de Piso Você Tem?
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            Nossa equipe técnica avalia no local e define o melhor método de limpeza
          </p>
          <Button 
            onClick={() => handleWhatsAppClick('info')}
            size="lg"
            className="bg-white text-green-600 hover:bg-gray-100 px-10 py-6 text-lg font-bold"
          >
            <MessageCircle className="w-5 h-5 mr-2" />
            Falar com Especialista
          </Button>
        </div>
      </section>

      {/* Conteúdos Relacionados */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Conteúdos Relacionados
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <Card className="border-2 border-gray-100 hover:border-green-500 hover:shadow-xl transition-all cursor-pointer" onClick={() => navigate('/limpeza-pos-obra')}>
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    Limpeza Pós-Obra Completa
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Conheça todos os nossos serviços de limpeza pós-obra
                  </p>
                  <span className="text-green-600 font-semibold text-sm flex items-center gap-2">
                    Ver serviços <ArrowRight className="w-4 h-4" />
                  </span>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-100 hover:border-green-500 hover:shadow-xl transition-all cursor-pointer" onClick={() => navigate('/blog/equipamentos-limpeza-pos-obra')}>
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    Equipamentos Profissionais
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Artigo completo sobre equipamentos de limpeza pós-obra
                  </p>
                  <span className="text-green-600 font-semibold text-sm flex items-center gap-2">
                    Ler artigo <ArrowRight className="w-4 h-4" />
                  </span>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-100 hover:border-green-500 hover:shadow-xl transition-all cursor-pointer" onClick={() => navigate('/locacao-lavadora-piso')}>
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    Locação de Equipamentos
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Prefere alugar os equipamentos? Veja as opções
                  </p>
                  <span className="text-blue-600 font-semibold text-sm flex items-center gap-2">
                    Ver locação <ArrowRight className="w-4 h-4" />
                  </span>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Perguntas Frequentes sobre Limpeza de Piso
              </h2>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {faqPiso.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="bg-slate-50 border-2 border-gray-100 rounded-lg px-6">
                  <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-green-600">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_locacao-pisos/artifacts/9nveujmi_file_00000000e2f071f5a40c0fc04e1c9bed.png" 
                alt="WellTec" 
                className="h-16 mb-4"
              />
              <p className="text-gray-400 text-sm">
                Limpeza de piso pós-obra especializada em BH.
              </p>
            </div>

            <div>
              <h3 className="font-bold mb-4">Serviços</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/limpeza-pos-obra" className="hover:text-green-400 transition-colors">Limpeza Pós-Obra</Link></li>
                <li><Link to="/limpeza-piso-pos-obra" className="hover:text-green-400 transition-colors">Limpeza de Piso</Link></li>
                <li><Link to="/locacao-lavadora-piso" className="hover:text-green-400 transition-colors">Locação</Link></li>
                <li><Link to="/blog" className="hover:text-green-400 transition-colors">Blog</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold mb-4">Empresa</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/" className="hover:text-green-400 transition-colors">Início</Link></li>
                <li><Link to="/servicos" className="hover:text-green-400 transition-colors">Serviços</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold mb-4">Contato</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>(31) 97182-3249</li>
                <li>BH e Região Metropolitana</li>
                <li>
                  <a href="https://www.instagram.com/welltecbrasil/" target="_blank" rel="noopener noreferrer" className="hover:text-green-400 transition-colors">
                    @welltecbrasil
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; 2025 WellTec - Limpeza de Piso Pós-Obra. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LimpezaPisoPosObra;
